// Each coin's last day of Hyperliquid 1m candles, fetched once (one request returns the whole
// day) and kept live by the candle stream while anything holds it. The wire reads the price at a
// headline's minute from it and the odds read their volatility. Floats: statistics for the
// screen, never money.

import { useEffect } from "react";
import { candles } from "./info";
import { onCandle, type Candle } from "./feed";
import { createStore, useStore } from "../state/store";
import { sigmaPerMinute as sigmaOf } from "../odds/model";

const MINUTE = 60_000;
/** A day of minutes, asked for on the first hold. */
const DAY = 1_440;
/** Bars kept: a day and a little more. */
const KEEP = 1_500;
/** The last four hours of closes make the volatility. */
const SIGMA_BARS = 240;
/** A minute with no bar of its own takes the bar before it, if that is this close. */
const NEAREST_MS = 3 * MINUTE;
/** A gap this long in the stream (a socket that slept) fetches the day again. */
const GAP_MS = 5 * MINUTE;
/** A released coin stays live this long, so a remount does not fetch again. */
const LINGER_MS = 15_000;
/** Ticks inside a minute bump the version at most this often. */
const TICK_MS = 1_000;

type Series = {
  /** Bar open times, ascending. */
  times: number[];
  closes: number[];
  sigma: number | null;
};

const series = new Map<string, Series>();
const holders = new Map<string, { count: number; release: () => void; linger: ReturnType<typeof setTimeout> | null }>();

/** Per coin, a number that goes up whenever a bar is added or its close moves. */
export const versions = createStore<Record<string, number>>({});
/** Per coin, the per-minute volatility of log returns over the last four hours; null until known. */
export const sigmas = createStore<Record<string, number | null>>({});

const dirty = new Set<string>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function flush() {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = null;
  if (dirty.size === 0) return;
  const coins = [...dirty];
  dirty.clear();
  versions.set((all) => {
    const next = { ...all };
    for (const coin of coins) next[coin] = (next[coin] ?? 0) + 1;
    return next;
  });
}

function bump(coin: string, now: boolean) {
  dirty.add(coin);
  if (now) flush();
  else flushTimer ??= setTimeout(flush, TICK_MS);
}

function recompute(coin: string, held: Series) {
  held.sigma = sigmaOf(held.closes.slice(-SIGMA_BARS));
  const sigma = held.sigma;
  sigmas.set((all) => (all[coin] === sigma ? all : { ...all, [coin]: sigma }));
}

/** Index of the last bar that opens at or before `t`, or -1. */
function atOrBefore(times: number[], t: number): number {
  let low = 0;
  let high = times.length - 1;
  let found = -1;
  while (low <= high) {
    const middle = (low + high) >> 1;
    if ((times[middle] ?? Infinity) <= t) {
      found = middle;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }
  return found;
}

/** Merges a fetched day under the bars the stream already brought (those are fresher). */
function merge(held: Series, bars: Candle[]) {
  const byTime = new Map<number, number>();
  for (const bar of bars) {
    const close = Number(bar.c);
    if (Number.isFinite(close) && close > 0) byTime.set(bar.t, close);
  }
  held.times.forEach((t, index) => {
    const close = held.closes[index];
    if (close !== undefined) byTime.set(t, close);
  });
  const ordered = [...byTime.entries()].sort((a, b) => a[0] - b[0]).slice(-KEEP);
  held.times = ordered.map(([t]) => t);
  held.closes = ordered.map(([, close]) => close);
}

/** A bar from the stream: the current minute moves, a new one is added. Returns true for a new bar. */
function put(held: Series, t: number, close: number): boolean {
  const last = held.times.length - 1;
  const lastTime = held.times[last];
  if (lastTime === undefined || t > lastTime) {
    held.times.push(t);
    held.closes.push(close);
    if (held.times.length > KEEP) {
      held.times.splice(0, held.times.length - KEEP);
      held.closes.splice(0, held.closes.length - KEEP);
    }
    return true;
  }
  const index = atOrBefore(held.times, t);
  if (index >= 0 && held.times[index] === t) {
    held.closes[index] = close;
    return false;
  }
  held.times.splice(index + 1, 0, t);
  held.closes.splice(index + 1, 0, close);
  return true;
}

function start(coin: string): () => void {
  const held: Series = { times: [], closes: [], sigma: null };
  series.set(coin, held);
  let alive = true;
  let loading = false;
  let controller = new AbortController();
  let retry: ReturnType<typeof setTimeout> | null = null;

  const load = (attempt = 0) => {
    if (!alive || loading) return;
    loading = true;
    controller = new AbortController();
    candles(coin, "1m", DAY, controller.signal).then(
      (bars) => {
        loading = false;
        if (!alive) return;
        merge(held, bars);
        recompute(coin, held);
        bump(coin, true);
      },
      () => {
        loading = false;
        // the stream keeps the series growing meanwhile; the day is asked for again
        if (alive && attempt < 4) retry = setTimeout(() => load(attempt + 1), 5_000 * 3 ** attempt);
      },
    );
  };
  load();

  const stop = onCandle(coin, "1m", (bar) => {
    const close = Number(bar.c);
    if (!Number.isFinite(close) || close <= 0) return;
    const lastTime = held.times[held.times.length - 1];
    const gap = lastTime !== undefined && bar.t - lastTime > GAP_MS;
    const added = put(held, bar.t, close);
    if (added) recompute(coin, held);
    bump(coin, added);
    if (gap) load();
  });

  return () => {
    alive = false;
    controller.abort();
    if (retry) clearTimeout(retry);
    stop();
    series.delete(coin);
    sigmas.set((all) => (coin in all ? { ...all, [coin]: null } : all));
  };
}

/** Holds `coin`'s history until the returned function is called. Counted, like `hold()`. */
export function holdHistory(coin: string): () => void {
  const entry = holders.get(coin);
  if (entry) {
    entry.count += 1;
    if (entry.linger) clearTimeout(entry.linger);
    entry.linger = null;
  } else {
    holders.set(coin, { count: 1, release: start(coin), linger: null });
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const held = holders.get(coin);
    if (!held) return;
    held.count -= 1;
    if (held.count > 0) return;
    held.linger = setTimeout(() => {
      const current = holders.get(coin);
      if (!current || current.count > 0) return;
      current.release();
      holders.delete(coin);
    }, LINGER_MS);
  };
}

/** Holds `coin`'s history while mounted; the returned version changes as its bars do. */
export function useHistory(coin: string | undefined): number {
  useEffect(() => (coin ? holdHistory(coin) : undefined), [coin]);
  return useStore(versions, (all) => (coin ? (all[coin] ?? 0) : 0));
}

/** The close of the 1m bar holding `ms`, or of the bar before it within three minutes; null outside the history. */
export function closeAt(coin: string, ms: number): number | null {
  const held = series.get(coin);
  if (!held) return null;
  const minute = Math.floor(ms / MINUTE) * MINUTE;
  const index = atOrBefore(held.times, minute);
  const t = held.times[index];
  if (index < 0 || t === undefined || minute - t > NEAREST_MS) return null;
  return held.closes[index] ?? null;
}

/** The latest close, live within the current minute. */
export function lastClose(coin: string): number | null {
  const held = series.get(coin);
  return held?.closes[held.closes.length - 1] ?? null;
}

/** When the history begins: headlines before it have no price to measure from. */
export function firstTime(coin: string): number | null {
  return series.get(coin)?.times[0] ?? null;
}

/** The last `count` closes, oldest first. */
export function closes(coin: string, count: number): number[] {
  if (count <= 0) return [];
  return series.get(coin)?.closes.slice(-count) ?? [];
}

/** Per-minute volatility of log returns over the last four hours, or null until known. */
export function sigmaPerMinute(coin: string): number | null {
  return series.get(coin)?.sigma ?? null;
}

/** The bars from `from` (ms) to now: open times and closes, oldest first. */
export function since(coin: string, from: number): { times: number[]; closes: number[] } {
  const held = series.get(coin);
  if (!held) return { times: [], closes: [] };
  const start = Math.max(0, atOrBefore(held.times, from));
  return { times: held.times.slice(start), closes: held.closes.slice(start) };
}
